/**
 * My Library Service
 * API wrapper for user library management endpoints
 *
 * Provides CRUD operations for user-uploaded content:
 * videos, documents, analysis reports, and YouTube imports.
 */

import axios from 'axios';
import config from '../utils/config';
import { getAuthToken } from '../utils/helpers';

const API_BASE = config.SERVER_BASE_URL;

/**
 * Get authorization headers for authenticated requests
 */
const getHeaders = () => ({
  Authorization: getAuthToken(),
  'Content-Type': 'application/json',
});

/**
 * My Library Service API
 */
export const myLibraryService = {
  /**
   * Get paginated library items with optional filters
   * @param {string} userId - User ID
   * @param {Object} options - Query options
   * @param {string} [options.type] - Filter by item type (video, document, analysis, report, youtube)
   * @param {string} [options.folderId] - Filter by folder ID
   * @param {string} [options.search] - Search query string
   * @param {number} [options.page=1] - Page number
   * @param {number} [options.limit=20] - Items per page
   * @param {string} [options.sort='created_at_desc'] - Sort order
   * @returns {Promise<{items: Array, total: number, page: number, totalPages: number}>}
   */
  async getItems(userId, { type, folderId, search, page = 1, limit = 20, sort = 'created_at_desc' } = {}) {
    const params = new URLSearchParams({ user_id: userId, page, limit, sort });
    if (type) params.append('type', type);
    if (folderId) params.append('folder_id', folderId);
    if (search) params.append('search', search);
    const res = await axios.get(`${API_BASE}/api/user/library?${params}`, { headers: getHeaders() });
    return res.data;
  },

  /**
   * Get a single library item by ID
   * @param {string} id - Library item ID
   * @returns {Promise<Object>} Library item details
   */
  async getItem(id) {
    const res = await axios.get(`${API_BASE}/api/user/library/${id}`, { headers: getHeaders() });
    return res.data;
  },

  /**
   * Delete a library item
   * @param {string} id - Library item ID
   * @returns {Promise<Object>} Deletion confirmation
   */
  async deleteItem(id) {
    const res = await axios.delete(`${API_BASE}/api/user/library/${id}`, { headers: getHeaders() });
    return res.data;
  },

  /**
   * Get storage usage statistics for a user
   * @param {string} userId - User ID
   * @returns {Promise<{used_bytes: number, total_bytes: number, breakdown: Object}>}
   */
  async getStorageUsage(userId) {
    const res = await axios.get(`${API_BASE}/api/user/storage-usage?user_id=${userId}`, { headers: getHeaders() });
    return res.data;
  },
};
